import { useEffect, useMemo, useRef, useState } from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  Textarea,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import { FaCheck, FaInbox, FaRegTrashAlt, FaTasks } from "react-icons/fa";
import {
  PENDING_STATUSES,
  ProcessPending,
  SaveProcessPendingCommand,
} from "../../../models/ProcessPending";
import {
  addProcessPending,
  deleteProcessPending,
  getProcessPendings,
  updateProcessPending,
} from "../../../services/processPendingService";
import { getUsers } from "../../../services/userService";
import { crm, Pill } from "../../../theme";
import SectionCard from "./SectionCard";
import { toneColors } from "./duimp";

const inputProps = {
  borderRadius: "10px",
  borderColor: crm.border,
  fontSize: "14px",
  bg: crm.surface,
  _hover: { borderColor: crm.borderStrong },
  focusBorderColor: crm.brand,
};

const Label = ({ children }: { children: React.ReactNode }) => (
  <FormLabel fontSize="12px" fontWeight="600" color={crm.textMuted} mb="6px">
    {children}
  </FormLabel>
);

interface UserOption {
  id: string;
  name: string;
}

interface Draft {
  id?: string;
  description: string;
  status: string;
  responsibleId: string;
  dueDate: string; // yyyy-MM-dd
}

const blank = (): Draft => ({ description: "", status: "Aguardando", responsibleId: "", dueDate: "" });

const dateLabel = (value?: string | null) => (value ? DateTime.fromISO(value).toFormat("dd/LL/yyyy") : "—");

const statusTone = (status: string) =>
  status === "Resolvida" ? toneColors.success : status === "Impedimento" ? toneColors.danger : toneColors.warning;

const isOverdue = (pending: ProcessPending) =>
  pending.isOpen && !!pending.dueDate && DateTime.fromISO(pending.dueDate).startOf("day") < DateTime.now().startOf("day");

/** Pendências do follow-up do processo (somente equipe interna). CRUD via api/process/{id}/pendings. */
const PendingsPanel = ({ processId }: { processId: string }) => {
  const [items, setItems] = useState<ProcessPending[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [draft, setDraft] = useState<Draft>(blank());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState<ProcessPending | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const toast = useToast();

  const load = async () => {
    setLoading(true);
    try {
      setItems(await getProcessPendings(processId));
    } catch {
      toast({ status: "error", title: "Não foi possível carregar as pendências" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [processId]);

  useEffect(() => {
    (async () => {
      const response = await getUsers();
      if (Array.isArray(response)) {
        setUsers(
          response
            // Responsável é sempre da equipe interna (position 0/1 = cliente)
            .filter((u: any) => u.position == null || u.position >= 2)
            .map((u: any) => ({ id: u.id, name: `${u.firstname ?? ""} ${u.lastname ?? ""}`.trim() || u.email }))
            .sort((a: UserOption, b: UserOption) => a.name.localeCompare(b.name))
        );
      }
    })();
  }, []);

  // Abertas primeiro (mais antigo vencimento primeiro), resolvidas por último.
  const sorted = useMemo(
    () =>
      [...items].sort((a, b) => {
        if (a.isOpen !== b.isOpen) return a.isOpen ? -1 : 1;
        return (a.dueDate ?? "9999").localeCompare(b.dueDate ?? "9999");
      }),
    [items]
  );
  const openCount = items.filter((i) => i.isOpen).length;

  const toCommand = (d: Draft): SaveProcessPendingCommand => ({
    description: d.description.trim(),
    status: d.status,
    responsibleId: d.responsibleId || null,
    dueDate: d.dueDate ? `${d.dueDate}T00:00:00` : null,
  });

  const run = async (action: () => Promise<{ success: boolean; notifications: string[] }>, okMessage: string) => {
    setSaving(true);
    try {
      const result = await action();
      if (!result.success) {
        toast({ status: "warning", title: result.notifications?.[0] ?? "Não foi possível concluir a operação" });
        return false;
      }
      toast({ status: "success", title: okMessage });
      await load();
      return true;
    } catch {
      toast({ status: "error", title: "Não foi possível concluir a operação" });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!draft.description.trim()) {
      toast({ status: "warning", title: "Informe a descrição da pendência" });
      return;
    }
    const command = toCommand(draft);
    const ok = await run(
      () => (draft.id ? updateProcessPending(draft.id, command) : addProcessPending(processId, command)),
      draft.id ? "Pendência atualizada" : "Pendência adicionada"
    );
    if (ok) setDraft(blank());
  };

  const resolve = (pending: ProcessPending) =>
    run(
      () =>
        updateProcessPending(pending.id, {
          description: pending.description,
          status: "Resolvida",
          responsibleId: pending.responsibleId ?? null,
          dueDate: pending.dueDate ?? null,
        }),
      "Pendência resolvida"
    );

  const confirmDelete = async () => {
    if (!toDelete) return;
    const target = toDelete;
    setToDelete(null);
    const ok = await run(() => deleteProcessPending(target.id), "Pendência excluída");
    if (ok && draft.id === target.id) setDraft(blank());
  };

  const edit = (pending: ProcessPending) =>
    setDraft({
      id: pending.id,
      description: pending.description,
      status: pending.status,
      responsibleId: pending.responsibleId ?? "",
      dueDate: pending.dueDate?.slice(0, 10) ?? "",
    });

  return (
    <SectionCard
      icon={<FaTasks />}
      title="Pendências do follow-up"
      subtitle="Impedimentos e itens aguardando resposta neste processo"
      actions={openCount > 0 && <Pill {...toneColors.warning}>{openCount} em aberto</Pill>}
    >
      {loading ? (
        <Flex justify="center" py="24px">
          <Spinner color={crm.brand} />
        </Flex>
      ) : sorted.length === 0 ? (
        <Flex direction="column" align="center" py="24px" color={crm.textFaint} gap="8px">
          <FaInbox fontSize="28px" />
          <Text fontSize="13px" color={crm.textMuted}>Nenhuma pendência registrada.</Text>
        </Flex>
      ) : (
        <Flex direction="column" gap="8px">
          {sorted.map((pending) => (
            <Flex
              key={pending.id}
              role="group"
              align="center"
              gap="12px"
              p="12px"
              border={`1px solid ${draft.id === pending.id ? crm.brand : crm.border}`}
              borderRadius={crm.radiusSm}
              bg={draft.id === pending.id ? crm.brandSoft : crm.surface}
              opacity={pending.isOpen ? 1 : 0.7}
              cursor="pointer"
              _hover={{ bg: crm.brandSoft }}
              onClick={() => edit(pending)}
            >
              <Box flex="1" minW={0}>
                <Text
                  fontSize="14px"
                  color={crm.text}
                  textDecoration={pending.isOpen ? undefined : "line-through"}
                  whiteSpace="pre-wrap"
                >
                  {pending.description}
                </Text>
                <Flex gap="12px" mt="4px" wrap="wrap" fontSize="12px" color={crm.textMuted}>
                  <Text>Responsável: {pending.responsibleName || "—"}</Text>
                  <Text color={isOverdue(pending) ? crm.danger : undefined} fontWeight={isOverdue(pending) ? 600 : undefined}>
                    Prazo: {dateLabel(pending.dueDate)}
                    {isOverdue(pending) ? " (atrasada)" : ""}
                  </Text>
                  {pending.resolvedAt && <Text>Resolvida em {dateLabel(pending.resolvedAt)}</Text>}
                </Flex>
              </Box>
              <Pill {...statusTone(pending.status)}>{pending.status}</Pill>
              {pending.isOpen && (
                <Tooltip label="Marcar como resolvida" hasArrow>
                  <IconButton
                    aria-label="Resolver pendência"
                    icon={<FaCheck />}
                    size="xs"
                    variant="ghost"
                    color={crm.success}
                    isDisabled={saving}
                    onClick={(event) => {
                      event.stopPropagation();
                      resolve(pending);
                    }}
                  />
                </Tooltip>
              )}
              <IconButton
                aria-label="Excluir pendência"
                icon={<FaRegTrashAlt />}
                size="xs"
                variant="ghost"
                color={crm.textFaint}
                opacity={0}
                _groupHover={{ opacity: 1 }}
                _hover={{ color: crm.danger, bg: "#fef2f2" }}
                onClick={(event) => {
                  event.stopPropagation();
                  setToDelete(pending);
                }}
              />
            </Flex>
          ))}
        </Flex>
      )}

      <Box mt="20px" p="16px" bg={crm.subtle} border={`1px solid ${crm.border}`} borderRadius={crm.radiusSm}>
        <Text fontSize="13px" fontWeight="700" color={crm.text} mb="12px">
          {draft.id ? "Editar pendência" : "Nova pendência"}
        </Text>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing="12px">
          <FormControl gridColumn={{ md: "span 3" }}>
            <Label>Descrição</Label>
            <Textarea
              {...inputProps}
              rows={2}
              maxLength={1000}
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            />
          </FormControl>
          <FormControl>
            <Label>Situação</Label>
            <Select {...inputProps} h="42px" value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>
              {PENDING_STATUSES.map((status) => (
                <option key={status} value={status}>{status}</option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <Label>Responsável</Label>
            <Select
              {...inputProps}
              h="42px"
              value={draft.responsibleId}
              onChange={(e) => setDraft({ ...draft, responsibleId: e.target.value })}
            >
              <option value="">Sem responsável</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>{user.name}</option>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <Label>Prazo</Label>
            <Input {...inputProps} h="42px" type="date" value={draft.dueDate} onChange={(e) => setDraft({ ...draft, dueDate: e.target.value })} />
          </FormControl>
        </SimpleGrid>
        <Flex mt="12px" justify="flex-end" gap="8px">
          {draft.id && (
            <Button variant="ghost" onClick={() => setDraft(blank())} isDisabled={saving}>
              Cancelar edição
            </Button>
          )}
          <Button
            bg={crm.brand}
            color="white"
            borderRadius="10px"
            _hover={{ bg: crm.brandDark }}
            isLoading={saving}
            onClick={save}
          >
            {draft.id ? "Salvar alterações" : "Adicionar pendência"}
          </Button>
        </Flex>
      </Box>

      <AlertDialog isOpen={!!toDelete} leastDestructiveRef={cancelRef} onClose={() => setToDelete(null)} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Excluir pendência</AlertDialogHeader>
            <AlertDialogBody>Excluir esta pendência do follow-up? Essa ação não pode ser desfeita.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setToDelete(null)}>Cancelar</Button>
              <Button colorScheme="red" ml={3} onClick={confirmDelete}>Excluir</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </SectionCard>
  );
};

export default PendingsPanel;
